import React, { useState, useEffect } from 'react';
import {
  UserCheck,
  Search,
  Filter,
  Eye,
  Ban,
  CheckCircle2,
  RefreshCw,
  X,
  Star,
  Lock,
  Unlock,
  ShieldCheck,
  ShieldAlert,
  CreditCard,
  FileText
} from 'lucide-react';
import { AdminCompanionItem } from '../../../types/admin.types';
import {
  fetchAdminCompanions,
  suspendCompanionApi,
  reactivateCompanionApi,
} from '../../../lib/adminService';
import { setCompanionSuspensionFlow } from '../../../utils/companionStatusManager';

interface CompanionsTabProps {
  onShowNotice: (msg: string, type: 'success' | 'error' | 'info') => void;
  onNavigateToKyc?: () => void;
}

export const CompanionsTab: React.FC<CompanionsTabProps> = ({ onShowNotice, onNavigateToKyc }) => {
  const [companions, setCompanions] = useState<AdminCompanionItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCompanion, setSelectedCompanion] = useState<AdminCompanionItem | null>(null);

  // Suspend action modal
  const [suspendModalCompanion, setSuspendModalCompanion] = useState<AdminCompanionItem | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  const loadCompanions = async () => {
    setIsLoading(true);
    try {
      const data = await fetchAdminCompanions(searchQuery, statusFilter);
      setCompanions(data);
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to load companion profiles', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCompanions();
  }, [statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadCompanions();
  };

  const handleConfirmSuspend = async () => {
    if (!suspendModalCompanion) return;
    if (!suspendReason.trim()) {
      onShowNotice('Reason for companion suspension is required', 'error');
      return;
    }

    setIsSubmittingAction(true);
    try {
      await suspendCompanionApi(suspendModalCompanion.id, suspendReason);
      await setCompanionSuspensionFlow(suspendModalCompanion.id, true, suspendReason);
      onShowNotice(`Companion ${suspendModalCompanion.name} suspended successfully.`, 'success');
      setSuspendModalCompanion(null);
      setSuspendReason('');
      loadCompanions();
    } catch (err: any) {
      onShowNotice(err.message || 'Error suspending companion', 'error');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleReactivate = async (comp: AdminCompanionItem) => {
    setIsSubmittingAction(true);
    try {
      await reactivateCompanionApi(comp.id);
      await setCompanionSuspensionFlow(comp.id, false);
      onShowNotice(`Companion profile for ${comp.name} reactivated.`, 'success');
      loadCompanions();
    } catch (err: any) {
      onShowNotice(err.message || 'Error reactivating companion', 'error');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-white tracking-tight">Companion Profiles & Operations</h2>
            <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono text-xs font-bold">
              {companions.length} Registered
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Section 8 & 9: Profile statuses, earnings, booking counts, and core lifecycle constraint enforcement.
          </p>
        </div>

        {/* Search & Filter */}
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search companion ID, name..."
              className="pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-purple-500 w-48 sm:w-64"
            />
          </form>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs font-bold text-slate-300 focus:outline-hidden cursor-pointer"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="SUSPENDED">Suspended</option>
          </select>

          <button
            type="button"
            onClick={loadCompanions}
            className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-purple-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Business Rule Notice (Section 9) */}
      <div className="p-4 rounded-2xl bg-purple-950/40 border border-purple-800/50 flex items-start gap-3">
        <Lock className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
        <div className="text-xs text-purple-200">
          <span className="font-bold text-white block">Lifecycle Invariant Enforced:</span>
          If a Companion has booking history, they cannot self-pause or self-delete in their portal. Only an Administrator with appropriate authority can modify their operational status.
        </div>
      </div>

      {/* Companions Table (Section 8) */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-950/60 text-slate-400 font-mono uppercase text-[10px] border-b border-slate-800">
              <tr>
                <th className="py-3 px-4">Companion ID</th>
                <th className="py-3 px-4">Name</th>
                <th className="py-3 px-4">Contact</th>
                <th className="py-3 px-4">Profile Status</th>
                <th className="py-3 px-4">KYC Status</th>
                <th className="py-3 px-4 text-center">Bookings</th>
                <th className="py-3 px-4">Rating</th>
                <th className="py-3 px-4">Earnings (₹)</th>
                <th className="py-3 px-4">Self-Pause Rule</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {companions.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-slate-500">
                    No companion records found.
                  </td>
                </tr>
              ) : (
                companions.map((comp) => (
                  <tr key={comp.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-3.5 px-4 font-mono font-bold text-purple-400">{comp.id}</td>
                    <td className="py-3.5 px-4 font-bold text-white">{comp.name}</td>
                    <td className="py-3.5 px-4">
                      <div className="text-slate-200">{comp.email}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{comp.phone}</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono border ${
                          comp.profileStatus === 'ACTIVE'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : comp.profileStatus === 'PAUSED'
                            ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                            : 'bg-rose-500/15 text-rose-400 border-rose-500/30'
                        }`}
                      >
                        {comp.profileStatus}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono border ${
                          comp.kycStatus === 'VERIFIED'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : comp.kycStatus === 'PENDING'
                            ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                            : 'bg-slate-800 text-slate-400 border-slate-700'
                        }`}
                      >
                        {comp.kycStatus}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-center font-bold text-white font-mono">
                      {comp.bookingCount}
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-1 text-amber-400 font-bold">
                        <Star className="w-3.5 h-3.5 fill-current" />
                        <span>{comp.rating.toFixed(1)}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 font-mono font-bold text-emerald-400">
                      ₹{comp.earnings.toLocaleString('en-IN')}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[10px]">
                      {comp.canSelfPauseOrDelete ? (
                        <span className="text-emerald-400 font-bold">Allowed (0 Bookings)</span>
                      ) : (
                        <span className="text-rose-400 font-bold">Blocked (Has Bookings)</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setSelectedCompanion(comp)}
                          className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition cursor-pointer"
                          title="View Details"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>

                        {comp.profileStatus === 'ACTIVE' ? (
                          <button
                            type="button"
                            onClick={() => {
                              setSuspendModalCompanion(comp);
                              setSuspendReason('');
                            }}
                            className="p-1.5 bg-rose-950/80 hover:bg-rose-900 border border-rose-800 text-rose-300 rounded-lg transition cursor-pointer"
                            title="Suspend Companion"
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleReactivate(comp)}
                            className="p-1.5 bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-800 text-emerald-300 rounded-lg transition cursor-pointer"
                            title="Reactivate Profile"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Companion Details Modal */}
      {selectedCompanion && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-xl w-full space-y-5">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-purple-400" />
                <h3 className="text-base font-black text-white">Companion: {selectedCompanion.name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedCompanion(null)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Companion ID</span>
                <span className="font-mono font-bold text-purple-300">{selectedCompanion.id}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Status</span>
                <span className="font-bold text-white">{selectedCompanion.profileStatus}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Total Net Earnings</span>
                <span className="font-mono font-bold text-emerald-400 text-sm">₹{selectedCompanion.earnings.toLocaleString('en-IN')}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Bookings Completed</span>
                <span className="font-bold text-white">{selectedCompanion.bookingCount}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">KYC Status</span>
                <span className="font-bold text-white">{selectedCompanion.kycStatus}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Rating</span>
                <span className="font-bold text-amber-400">★ {selectedCompanion.rating.toFixed(1)} / 5.0</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-3.5 bg-slate-950 rounded-xl border border-slate-800 text-xs">
              <span className="text-slate-400">Need to inspect KYC documentation?</span>
              <button
                type="button"
                onClick={() => {
                  setSelectedCompanion(null);
                  if (onNavigateToKyc) onNavigateToKyc();
                }}
                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-bold text-xs cursor-pointer flex items-center gap-1.5"
              >
                <FileText className="w-3.5 h-3.5" />
                <span>Go to KYC Desk</span>
              </button>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setSelectedCompanion(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Suspend Action */}
      {suspendModalCompanion && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-rose-900/60 rounded-3xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-2 text-rose-400 font-bold text-sm">
              <ShieldAlert className="w-5 h-5" />
              <span>Confirm Companion Suspension</span>
            </div>
            <p className="text-xs text-slate-300">
              Suspend companion <strong>{suspendModalCompanion.name}</strong> ({suspendModalCompanion.id})? Profile will immediately be hidden from search and unable to accept bookings.
            </p>
            <div>
              <label className="block text-[11px] font-bold text-slate-400 mb-1">
                Mandatory Reason for Suspension: *
              </label>
              <textarea
                rows={3}
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="e.g. Conduct violation, failed quality audit..."
                className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-rose-500"
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                disabled={isSubmittingAction}
                onClick={() => setSuspendModalCompanion(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmittingAction || !suspendReason.trim()}
                onClick={handleConfirmSuspend}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold cursor-pointer disabled:opacity-50"
              >
                {isSubmittingAction ? 'Processing...' : 'Confirm Suspension'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
